import styled from '@emotion/styled'
import { isDefaultBidderRuleFor } from '@pubstack/common/src/bidder-rule'
import { isInappEnv } from '@pubstack/common/src/site'
import { CreateStack } from '@pubstack/common/src/stack'
import { useQueryClient } from '@tanstack/react-query'
import { FunctionComponent, useMemo } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { useBidderSeats } from '~/api/bidder-seats.api'
import { STACKS_QUERY_KEYS, useCreateStack } from '~/api/stacks.api'
import { Colors } from '~/assets/style/colors'
import { useScopePermissions } from '~/auth/useScopePermissions'
import { IllustrationMessage } from '~/components/IllustrationMessage'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { OnboardingCard, OnboardingCardProps } from '~/components/OnboardingCard'
import { Skeleton } from '~/components/Skeleton'
import { useToast } from '~/components/Toast/useToasts'
import { CreateAdUnitsFlyout } from '~/modules/pbs/adunits/CreateAdUnitsFlyout'
import { PbsSitePageContext } from '../useSitePageContext'

const TRACKING_LABEL = 'pbs-sites-activation'
const FLYOUT_WIDTH = '600px'
const ILLUSTRATION_ICON_SIZE_PX = 190

const PbsOnboardingCardsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
`

export const ActivationContent: FunctionComponent<PbsSitePageContext> = ({ adUnits, site, stacks, bidderRules, isLoading }) => {
  const { canCreate } = useScopePermissions()
  // API Hooks
  const { data: bidderSeats, isLoading: isBidderSeatsLoading } = useBidderSeats('pbs')
  const { mutateAsync: createStack, isPending: isStackCreationPending } = useCreateStack()

  // Hooks
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const flyout = useGlobalFlyout()
  const toast = useToast()
  const isMapped = useMemo(() => Object.entries(site.mappingCountPerBidderSeat ?? {}).some(([, count]) => count > 0), [site.mappingCountPerBidderSeat])
  const isInapp = isInappEnv(site.env)
  const handleNewAdUnitClick = () => {
    if (site.env === 'web')
      return flyout.open(CreateAdUnitsFlyout, { color: Colors.Kaiminus, width: FLYOUT_WIDTH, closeTrackingLabel: `${TRACKING_LABEL}-adUnit-create-close` }, { onClose: () => flyout.close() })
    navigate(`/monetization/node/adunits/new/${isInapp ? 'inapp' : 'amp'}`)
  }

  const handleViewAdUnitsClick = () => {
    navigate(`/monetization/inventory/adunits/${isInapp ? 'inapp' : site.env === 'amp' ? 'amp' : 'server-side'}`)
  }

  const handleCatalogClick = () => {
    const route = bidderSeats.length ? 'my-bidder-seats' : 'catalog'
    // Both pages are product-neutral now. This is the only place that sets `backToSite`, which is why
    // they can send the user back to a Node media without carrying a product in the URL.
    navigate(`/monetization/inventory/${route}?backToSite=${site.id}`)
  }

  const handleS2SConnectionClick = () => {
    navigate(`/monetization/node/sites/${site.id}/connect-to-pubstack`)
  }

  const serverToSiteCard: OnboardingCardProps = {
    name: 'Server to site connection',
    description: 'Get your configuration details to integrate Prebid Server monetisation in your media.',
    buttonsProps: [
      {
        label: 'View integration',
        trackingLabel: `${TRACKING_LABEL}-connect-to-pubstack-navigate`,
        variant: 'primary',
        onClick: handleS2SConnectionClick,
      },
    ],
    disabled: !adUnits.length,
  }

  const pbsOnboardingCards: OnboardingCardProps[] = [
    ...(adUnits.length > 0 ? [serverToSiteCard] : []),
    {
      name: 'Ad units',
      description: "Build your inventory by creating ad units. Copy the code for each ad unit into your media's pbjs.config configuration file.",
      buttonsProps: [
        ...(adUnits.length
          ? ([
              {
                label: `View all ad units (${adUnits.length})`,
                trackingLabel: `${TRACKING_LABEL}-ad-unit-navigate`,
                variant: 'tertiary',
                onClick: handleViewAdUnitsClick,
              },
            ] as OnboardingCardProps['buttonsProps'])
          : []),
        /* Creating inventory is the owner's, and the API refuses it to anyone else — this button was the one
           place in the app that offered it to a plain user, which is how they could create an ad unit at all. */
        ...(canCreate
          ? ([
              {
                label: 'Add ad unit',
                trackingLabel: `${TRACKING_LABEL}-ad-unit-new`,
                iconName: 'add_adUnit',
                variant: adUnits.length ? 'secondary' : 'primary',
                onClick: handleNewAdUnitClick,
              },
            ] as OnboardingCardProps['buttonsProps'])
          : []),
      ],
      disabled: false,
    },
    {
      name: 'Bidders',
      description: 'Set the bidders to use on this media.',
      buttonsProps: [
        {
          label: bidderSeats.length ? 'Map bidders' : 'See catalog',
          trackingLabel: `${TRACKING_LABEL}-catalog-navigate`,
          iconName: 'bidder',
          variant: isMapped ? 'secondary' : 'primary',
          onClick: handleCatalogClick,
        },
      ],
      disabled: !adUnits.length,
    },
    {
      name: 'Stack',
      description: 'Create stack and set the bidder rule to use on this media.',
      buttonsProps: [
        ...(stacks.length > 0
          ? ([
              {
                label: 'View stack',
                trackingLabel: `${TRACKING_LABEL}-stack-navigate`,
                variant: 'tertiary',
                onClick: () => {
                  navigate(`/monetization/node/sites/${site.id}/stacks`)
                },
              },
            ] as OnboardingCardProps['buttonsProps'])
          : ([
              {
                label: 'Add stack',
                trackingLabel: `${TRACKING_LABEL}-stack-new`,
                iconName: 'add',
                variant: 'primary',
                onClick: async () => {
                  const defaultBidderRule = bidderRules.find((rule) => isDefaultBidderRuleFor(rule, 'pbs'))
                  if (!defaultBidderRule) {
                    toast.alert('Could not create stack, no default bidder rule was found.')
                    return
                  }

                  // Create the stack
                  const stackToCreate: CreateStack = {
                    type: 'pbs',
                    siteId: site.id,
                    name: 'Default',
                    configuration: {
                      contextId: null,
                      bidderRuleId: defaultBidderRule.id,
                    },
                  }
                  await createStack(stackToCreate)

                  queryClient.invalidateQueries({ queryKey: [...STACKS_QUERY_KEYS, { siteId: site.id }] })
                  toast.success(
                    <>
                      <strong>Default</strong> stack created.
                    </>
                  )
                  navigate(`/monetization/node/sites/${site.id}/stacks`)
                },
              },
            ] as OnboardingCardProps['buttonsProps'])),
      ],
      disabled: !isMapped || isLoading || isStackCreationPending,
    },
    ...(!adUnits.length ? [serverToSiteCard] : []),
  ]

  return (
    <>
      {isLoading || isBidderSeatsLoading ? (
        <Skeleton bigger />
      ) : site.pbsStandaloneStatus === 'inactive' ? (
        <IllustrationMessage
          iconName="night_sky"
          iconSize={`${ILLUSTRATION_ICON_SIZE_PX}px`}
          title="This media is not on Node yet."
          message="Edit the media from the media list to enable Node on it."
        />
      ) : (
        <PbsOnboardingCardsWrapper>
          <p>Initiate your configuration</p>

          {pbsOnboardingCards.map((card, index) => (
            <OnboardingCard key={index} name={card.name} description={card.description} buttonsProps={card.buttonsProps} disabled={card.disabled} />
          ))}
        </PbsOnboardingCardsWrapper>
      )}
    </>
  )
}

export const Activation: FunctionComponent = () => {
  const context = useOutletContext<PbsSitePageContext>()

  return <ActivationContent {...context} />
}
